/**
 * Website settings, shared across the whole site.
 *
 * Fetched once at the top of the tree rather than per component — the header,
 * footer, hero, contact page and every trust figure need the same object, and
 * eight independent requests for it would be wasteful and could disagree with
 * each other mid-render.
 *
 * The bundled constants are the initial value, so the first paint is immediate
 * and correct-looking; remote values replace them a moment later. There is no
 * loading state here on purpose — a header that renders blank while settings
 * load is worse than one that briefly shows the previous phone number.
 */
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { siteSettingsService } from "../services/siteSettingsService";
import { SITE_SETTINGS } from "../data/site";
import { setBrand } from "../lib/siteConfig";

const SiteSettingsContext = createContext(SITE_SETTINGS);

export function SiteSettingsProvider({ children }) {
  const [settings, setSettings] = useState(SITE_SETTINGS);

  useEffect(() => {
    let active = true;

    siteSettingsService
      .get()
      .then((remote) => {
        if (!active || !remote) return;
        setSettings(remote);
        // Keep the non-React store in step, so WhatsApp links built outside
        // React use the current number too.
        setBrand(remote.brand);
      })
      .catch(() => {
        // The service already falls back to bundled settings; nothing to do
        // beyond keeping what we have.
      });

    return () => {
      active = false;
    };
  }, []);

  return (
    <SiteSettingsContext.Provider value={settings}>{children}</SiteSettingsContext.Provider>
  );
}

/** Full settings object. */
export function useSiteSettings() {
  return useContext(SiteSettingsContext);
}

/** Just the brand block — what most components actually want. */
export function useBrand() {
  const settings = useContext(SiteSettingsContext);
  return useMemo(() => settings?.brand ?? SITE_SETTINGS.brand, [settings]);
}
